import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("loyalty_token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios
        .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => setUser(res.data))
        .catch(() => {
          localStorage.removeItem("loyalty_token");
          setToken(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = (newToken, userData) => {
    localStorage.setItem("loyalty_token", newToken);
    setToken(newToken);
    setUser(userData);
  };

  const logout = () => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    axios.post(`${API}/auth/logout`, {}, { headers, withCredentials: true }).catch(() => {});
    localStorage.removeItem("loyalty_token");
    localStorage.removeItem("loyalty_refresh");
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      setUser(res.data);
    } catch {}
  };

  const authHeaders = () => ({ Authorization: `Bearer ${token}` });

  const isAdmin = user?.role === "admin";
  const isVendor = user?.role === "vendor";
  const isStaff = user?.role === "staff";
  const needsOnboarding = isVendor && !user?.vendor_id;
  const permissions = user?.permissions || (
    isVendor || isAdmin
      ? { can_add_points: true, can_issue_reward: true, can_redeem: true }
      : { can_add_points: false, can_issue_reward: false, can_redeem: false }
  );
  const defaultPath = isStaff ? "/scan" : needsOnboarding ? "/onboarding" : "/dashboard";

  return (
    <AuthContext.Provider value={{
      user, token, loading, login, logout, authHeaders, refreshUser,
      isAdmin, isVendor, isStaff, needsOnboarding, permissions, defaultPath,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export default AuthContext;
